import {
  checkboxClasses,
  formControlLabelClasses,
  typographyClasses,
} from '@mui/material';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { type JSX, useCallback } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { AutocompleteFormField } from '~components/FormFields/AutocompleteFormField';
import CheckboxInput from '~components/FormFields/v2/CheckboxInput';
import { AnalyticsController } from '~hooks/useAnalytics/AnalyticsController';
import type { SiteRateSchedule } from '~hooks/useSiteRateSchedule/models';
import {
  getHaulerRateTypeOptions,
  getRateTypeLabel,
} from '~pages/Settings/RatesManagement/rateUtils';
import type { Nullable } from '~types/Nullable';

import FreightRates from '../../components/FreightRates';
import type { ProjectFormSchemaInterface } from '../../projectFormSchema';
import { useProjectFormV2Context } from '../ProjectFormV2Context';

// Module-scoped empty arrays/values reused across renders so the panel
// passes referentially stable props to <FreightRates/> when the source data
// (project add-on assignments, site rate schedules, etc.) isn't set yet.
// Without stable identity, the panel would hand fresh `[]` each render and
// FreightRates' internal effects would refire on every Provider tick.
const EMPTY_SITE_RATE_SCHEDULES: SiteRateSchedule[] = [];
const NULL_SITE_RATE_SCHEDULE_ID: Nullable<string> = null;
const NULL_RATES_SYNC_STATUS: 'synced' | 'stamped' | null = null;
const NULL_SOURCE_STARRED_SRS: Nullable<SiteRateSchedule> = null;

// This panel owns the full rate section, mirroring the legacy form. When
// neither rate-card flag is on it renders the
// rule-based <FreightRates/> UI (the `!shouldShowHaulerRateCard` branch); when
// showHaulerRateTypeCard OR tomlinsonOrderModal is on it renders the compact
// hauler-rate-card widget instead (the alternative surface deferred to a
// later pass).
//
// Wiring discipline for the FreightRates branch (a type-level gate, enforced
// by panels/__tests__/
// FreightRatesPanel.test.tsx):
//
//   1. Every prop in `React.ComponentProps<typeof FreightRates>` is wired
//      with a defined, non-`any` value.
//   2. Callbacks come from `useProjectFormV2Context().callbacks`, never
//      stubbed inline as `any` and never undefined.
//   3. Identity-stable defaults (frozen arrays, stable noop callbacks) so
//      FreightRates' internal effects don't churn on Provider re-renders.
//
// Per-prop wiring table:
//
//   defaultCustomerRateDetails        — defaultProject.customerRate* (edit) / preloads.primaryCustomerRate (create)
//   defaultVendorRateDetails          — defaultProject.vendorRate* (edit) / null (create)
//   defaultDriverRateDetails          — defaultProject.driverRate* (edit) / null (create)
//   disableTargetRateControls         — !watchedSelectedCompany?.id
//   isEditingOrder                    — false (project form, not order form)
//   isCopyAndEdit                     — ctx.isCopyAndEdit (carries source rates over, strips SRS ids)
//   stripSrsIds                       — true (copying a project always recreates SRS)
//   initiallyExposeDriverRate         — false (the order form expands it, the project form does not)
//   fetchCustomerRates                — callbacks.fetchCustomerRates
//   fetchVendorRates                  — callbacks.fetchVendorRates
//   fetchDriverRates                  — callbacks.fetchDriverRates
//   fetchParentAddOnAssignments       — callbacks.fetchParentAddOnAssignments
//   projectCustomerAddOnAssignments   — EMPTY_ADD_ON_ASSIGNMENTS
//   projectVendorAddOnAssignments     — EMPTY_ADD_ON_ASSIGNMENTS
//   projectDriverAddOnAssignments     — EMPTY_ADD_ON_ASSIGNMENTS
//   siteRateCustomerAddOnAssignments  — EMPTY_ADD_ON_ASSIGNMENTS
//   siteRateVendorAddOnAssignments    — EMPTY_ADD_ON_ASSIGNMENTS
//   siteRateDriverAddOnAssignments    — EMPTY_ADD_ON_ASSIGNMENTS
//   alwaysShowDriverRate              — true (mirrors the legacy form)
//   onDirtyStatusChange               — callbacks.onDirtyStatusChange
//   showManageSiteRatesButton         — true (mirrors the legacy form)
//   ratesSyncStatus                   — null (order-only signal)
//   projectId                         — sourceProjectId ?? defaultProject?.id ?? '' (fetch source SRS on copy)
//   useVendorRateForDriver            — false (order-only)
//   setUseVendorRateForDriver         — stable noop
//   initialSiteRateSchedules          — EMPTY_SITE_RATE_SCHEDULES
//   bottomContent                     — <CheckboxInput name="prevailingWage" />
//   accountId                         — watchedAccount?.id ?? ''
//   prevailingWage                    — Boolean(watchedPrevailingWage)
//   defaultSiteRateScheduleId         — null
//   onDefaultSiteRateScheduleChange   — stable noop
//   sourceStarredSiteRateSchedule     — null
//   scope                             — 'project'
//   onRateCardsLoadedChange           — ctx.onRateCardsLoadedChange (gates the dialog's quote-preview CTA)
export function FreightRatesPanel(): JSX.Element | null {
  const {
    control,
    formState: { errors },
  } = useFormContext<ProjectFormSchemaInterface>();
  // Selective rate apply: the persisted toggle drives whether a cascading rate edit opens the
  // impact dialog at submit. Watched so a change within the same form session takes effect.
  const watchedApplyRateChangesToExistingOrders = useWatch({
    control,
    name: 'applyRateChangesToExistingOrders',
  });
  const ctx = useProjectFormV2Context();
  // freightRatesRef lives on the Provider so the form shell can call
  // .onSubmit on it at submit time to extract the rule-based UI's
  // site-rate-schedule operations (+ optional add-on assignments). A
  // panel-local ref would be unreachable from the shell, which is the
  // bug we just hit: no rate data ever reached the dialog's pipeline.
  const freightRatesRef = ctx.freightRatesRef;

  // Stable identity for the order-only onDefaultSiteRateScheduleChange
  // callback. The project form never reads back through it — it's a noop to
  // satisfy the type-level "every callback is defined and non-any" gate
  // without leaking a fresh closure per render.
  // JS arity rules: a 0-param fn is assignable to (id)=>void.
  const onDefaultSiteRateScheduleChange = useCallback(() => {
    /* project form does not change defaultSiteRateScheduleId */
  }, []);

  // The rate section swaps between the rule-based FreightRates UI and a compact
  // hauler-rate-card widget, mirroring the legacy form. The
  // swap fires when EITHER showHaulerRateTypeCard or tomlinsonOrderModal is on
  // (legacy `shouldShowHaulerRateCard`); the haulerRateType field is only
  // required under tomlinsonOrderModal (mirrors the legacy form) — validation
  // stays on the Tomlinson bundle.
  const shouldShowHaulerRateCard =
    ctx.flags.showHaulerRateTypeCard || ctx.flags.tomlinsonOrderModal;
  const haulerRateCardHeader = (
    <>
      <Typography variant="form-section-title" sx={{ mb: 1 }}>
        {t('order.form.rate_details')}
      </Typography>

      <Grid container spacing={2} sx={{ mb: ctx.keepFreightRatesMounted ? 2 : 0 }}>
        <Grid size={{ xs: 6 }}>
          <AutocompleteFormField
            clearable
            control={control}
            disablePortal={false}
            errors={errors}
            getLabel={(item) => item?.label ?? getRateTypeLabel(item)}
            getValue={(item) => item?.value ?? item}
            isCheckEnabled={false}
            isRequired={ctx.flags.tomlinsonOrderModal}
            label={t('form_fields.hauler_rate_type')}
            list={getHaulerRateTypeOptions()}
            name="haulerRateType"
          />
        </Grid>
      </Grid>
    </>
  );
  // A required freight rate is validated inside FreightRates, so the hauler card
  // cannot replace it.
  if (shouldShowHaulerRateCard && !ctx.keepFreightRatesMounted) {
    return (
      <Card sx={{ p: 2 }}>
        {haulerRateCardHeader}
        <CheckboxInput
          control={control}
          label={t('project.form.prevailing_wage')}
          name="prevailingWage"
          sx={{
            [`& .${formControlLabelClasses.root}`]: { mr: 0 },
            [`& .${checkboxClasses.root}`]: { pr: '2px', py: 0 },
            [`& .${typographyClasses.root}`]: {
              fontSize: '12px',
              fontWeight: 400,
            },
          }}
        />
      </Card>
    );
  }

  const isEditingProject = Boolean(ctx.defaultProject?.id);
  const isCopyAndEdit = ctx.isCopyAndEdit;
  // Copy-and-edit seeds the rate details from the source project just like an
  // edit does (the legacy form gates on
  // `isEditingProject || isCopyAndEdit`). On copy the project id is stripped,
  // so `isEditingProject` is false; without this OR the panel would fall back
  // to the create path and show "No rates set" (the copy-and-edit bug).
  const seedRatesFromSource = isEditingProject || isCopyAndEdit;

  // Mirrors the legacy form. For edit / copy-and-edit use the
  // existing project's persisted rate; for create mode use the preloaded
  // primary rate (only the customer side has a primary fallback —
  // vendor/driver default to null on create). targetRateType is `RateType`
  // from the API spec; the legacy `as RateType` cast is preserved
  // structurally.
  const defaultDriverRateDetails = {
    targetRate: seedRatesFromSource ? (ctx.defaultProject?.driverRate ?? null) : null,
    targetRateType: seedRatesFromSource
      ? (ctx.defaultProject?.driverRateType ?? null)
      : null,
    targetRateValue: seedRatesFromSource
      ? (ctx.defaultProject?.driverRateValue ?? null)
      : null,
  };

  // Legacy `projectIdForDataFetching = sourceProjectId ?? defaultProject?.id`.
  // On copy the source id is what FreightRates uses to
  // fetch the original site-rate-schedules so they can be recreated (stripped
  // of ids via isCopyAndEdit + stripSrsIds) under the new project.
  const projectIdForDataFetching = ctx.sourceProjectId ?? ctx.defaultProject?.id ?? '';

  return (
    <Card sx={{ p: 2 }}>
      {shouldShowHaulerRateCard && haulerRateCardHeader}
      <FreightRates
        ref={freightRatesRef}
        companyId={ctx.watchedSelectedCompany?.id ?? ctx.defaultProject?.company?.id}
        isFreightRateRequired={ctx.isFreightRateRequired}
        defaultDriverRateDetails={defaultDriverRateDetails}
        isEditingOrder={false}
        isCopyAndEdit={isCopyAndEdit}
        stripSrsIds={true}
        initiallyExposeDriverRate={false}
        alwaysShowDriverRate={true}
        onDirtyStatusChange={ctx.callbacks.onDirtyStatusChange}
        ratesSyncStatus={NULL_RATES_SYNC_STATUS}
        projectId={projectIdForDataFetching}
        useVendorRateForDriver={false}
        initialSiteRateSchedules={EMPTY_SITE_RATE_SCHEDULES}
        bottomContent={
          /* Single flex child: FreightRates' root column applies its card-sized
             gap between each child, which read as excessive padding between
             these two related checkboxes. */
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <CheckboxInput
              control={control}
              label={t('project.form.prevailing_wage')}
              name="prevailingWage"
              sx={{
                [`& .${formControlLabelClasses.root}`]: { mr: 0 },
                [`& .${checkboxClasses.root}`]: { pr: '2px', py: 0 },
                [`& .${typographyClasses.root}`]: {
                  fontSize: '12px',
                  fontWeight: 400,
                },
              }}
            />
            <Tooltip
              title={t('project.form.apply_rate_changes_to_existing_orders_tooltip')}
            >
              {/* fit-content so the hover zone ends at the label, not the row */}
              <Box sx={{ width: 'fit-content' }}>
                <CheckboxInput
                  control={control}
                  label={t('project.form.apply_rate_changes_to_existing_orders')}
                  name="applyRateChangesToExistingOrders"
                  onValueChange={(checked) =>
                    AnalyticsController.track('apply_rate_changes_toggled', {
                      scope: 'project',
                      checked,
                    })
                  }
                  sx={{
                    [`& .${formControlLabelClasses.root}`]: { mr: 0 },
                    [`& .${checkboxClasses.root}`]: { pr: '2px', py: 0 },
                    [`& .${typographyClasses.root}`]: {
                      fontSize: '12px',
                      fontWeight: 400,
                    },
                  }}
                />
              </Box>
            </Tooltip>
          </Box>
        }
        accountId={ctx.watchedAccount?.id ?? ''}
        prevailingWage={Boolean(ctx.watchedPrevailingWage)}
        defaultSiteRateScheduleId={NULL_SITE_RATE_SCHEDULE_ID}
        onDefaultSiteRateScheduleChange={onDefaultSiteRateScheduleChange}
        sourceStarredSiteRateSchedule={NULL_SOURCE_STARRED_SRS}
        scope="project"
        onRateCardsLoadedChange={ctx.onRateCardsLoadedChange}
        applyRateChangesToExistingOrders={Boolean(
          watchedApplyRateChangesToExistingOrders,
        )}
      />
    </Card>
  );
}
